import apiClient from '../apiClient';

/**
 * Production-ready Company/Tenant API Service
 * Integrates with backend TenantQueryController and TenantRegistrationCommandController
 */

// ==================== QUERY ENDPOINTS ====================

/**
 * Get all tenants (ADMIN only)
 * Endpoint: GET /api/tenants
 * Requires: ADMIN role
 */
export const getAllTenants = async (params = {}) => {
    try {
        console.log('[CompanyAPI] Fetching all tenants from backend...', params);
        const response = await apiClient.get('/tenants', { params });
        console.log('[CompanyAPI] Successfully fetched tenants:', response.data.content ? response.data.content.length : response.data.length);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Error fetching all tenants:', error);
        if (error.response?.status === 403) {
            throw new Error('Access denied.');
        }
        throw error;
    }
};

/**
 * Get current tenant information
 * Endpoint: GET /api/tenants/current
 * Requires: USER, TENANT_ADMIN, TENANT_OWNER, ADMIN, or OWNER role
 */
export const getCurrentTenant = async () => {
    try {
        console.log('[CompanyAPI] Fetching current tenant...');
        const response = await apiClient.get('/tenants/current');
        console.log('[CompanyAPI] Current tenant:', response.data);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Error fetching current tenant:', error);
        throw error;
    }
};

/**
 * Get all users in current tenant
 * Endpoint: GET /api/tenants/current/users
 * Requires: TENANT_ADMIN, TENANT_OWNER, ADMIN, or OWNER role
 */
export const getCurrentTenantUsers = async () => {
    try {
        console.log('[CompanyAPI] Fetching current tenant users...');
        const response = await apiClient.get('/tenants/current/users');
        console.log('[CompanyAPI] Tenant users count:', response.data.length);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Error fetching tenant users:', error);
        throw error;
    }
};

/**
 * Get tenant statistics
 * Endpoint: GET /api/tenants/current/stats
 * Requires: TENANT_ADMIN, TENANT_OWNER, or ADMIN role
 */
export const getTenantStats = async () => {
    try {
        console.log('[CompanyAPI] Fetching tenant statistics...');
        const response = await apiClient.get('/tenants/current/stats');
        console.log('[CompanyAPI] Tenant stats:', response.data);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Error fetching tenant stats:', error);
        throw error;
    }
};

/**
 * Get tenant context information
 * Endpoint: GET /api/tenants/context
 * Requires: TENANT_ADMIN, TENANT_OWNER, ADMIN, or USER role
 */
export const getTenantContext = async () => {
    try {
        console.log('[CompanyAPI] Fetching tenant context...');
        const response = await apiClient.get('/tenants/context');
        console.log('[CompanyAPI] Tenant context:', response.data);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Error fetching tenant context:', error);
        throw error;
    }
};

// ==================== REGISTRATION ENDPOINTS ====================

/**
 * Register a new tenant with owner user
 * Endpoint: POST /api/public/register
 * No authentication required
 * 
 * @param {Object} tenantData - Registration data
 * @returns {Promise<TenantRegistrationResult>}
 */
export const registerTenant = async (tenantData) => {
    try {
        console.log('[CompanyAPI] Registering new tenant:', tenantData.companyName);

        // Map frontend data to backend RegisterTenantCommand
        const command = {
            // Company Details
            companyName: tenantData.companyName,
            companyEmail: tenantData.companyEmail,
            companyPhone: tenantData.companyPhone,
            companyAddress: tenantData.companyAddress,
            taxId: tenantData.taxId || tenantData.tradeLicenseNo,

            // Owner Details
            ownerFirstName: tenantData.ownerFirstName,
            ownerLastName: tenantData.ownerLastName,
            ownerEmail: tenantData.ownerEmail,
            ownerUsername: tenantData.ownerUsername,
            ownerPassword: tenantData.ownerPassword,
            ownerPhone: tenantData.ownerPhone,

            // Subscription
            subscriptionPlan: tenantData.subscriptionPlan?.toUpperCase() || 'BASIC',

            // WhatsApp Integration (optional)
            whatsappPhoneNumber: tenantData.whatsappPhoneNumber,
            whatsappBusinessAccountId: tenantData.whatsappBusinessAccountId,
            whatsappPhoneNumberId: tenantData.whatsappPhoneNumberId,
            whatsappAccessToken: tenantData.whatsappAccessToken,

            // Additional metadata
            metadata: tenantData.metadata ? JSON.stringify(tenantData.metadata) : null
        };

        const response = await apiClient.post('/public/register', command);
        console.log('[CompanyAPI] Registration successful:', response.data);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Registration failed:', error);
        if (error.response?.status === 409) {
            throw new Error('Company or user already exists. Please use different email/username.');
        }
        throw error;
    }
};

/**
 * Register a sample test tenant (TEST ONLY)
 * Endpoint: POST /api/public/test/register-sample
 * No authentication required
 */
export const registerSampleTenant = async () => {
    try {
        console.log('[CompanyAPI] Registering sample test tenant...');
        const response = await apiClient.post('/public/test/register-sample');
        console.log('[CompanyAPI] Sample tenant registered:', response.data);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Sample registration failed:', error);
        throw error;
    }
};

/**
 * Get subscription plans
 * Endpoint: GET /api/public/subscription-plans
 * No authentication required
 */
export const getSubscriptionPlans = async () => {
    try {
        console.log('[CompanyAPI] Fetching subscription plans...');
        const response = await apiClient.get('/public/subscription-plans');
        console.log('[CompanyAPI] Subscription plans:', response.data);
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Error fetching subscription plans:', error);
        // Return default plans if backend endpoint not available
        return [
            { id: 'BASIC', name: 'Basic', maxUsers: 1, maxOrders: 100, maxProducts: 10 },
            { id: 'STANDARD', name: 'Standard', maxUsers: 5, maxOrders: 1000, maxProducts: 50 },
            { id: 'PROFESSIONAL', name: 'Professional', maxUsers: 20, maxOrders: 10000, maxProducts: 200 },
            { id: 'ENTERPRISE', name: 'Enterprise', maxUsers: -1, maxOrders: -1, maxProducts: -1 }
        ];
    }
};

// ==================== LEGACY COMPATIBILITY ====================

/**
 * Legacy method for backward compatibility
 * Maps to getAllTenants()
 */
export const getCompanies = getAllTenants;

/**
 * Legacy method for backward compatibility
 * Maps to registerTenant()
 */
export const registerCompany = registerTenant;

/**
 * Get company by ID
 * TODO: Backend endpoint not yet available
 */
export const getCompanyById = async (id) => {
    console.warn('[CompanyAPI] getCompanyById not implemented in backend yet');
    throw new Error('Backend endpoint not yet available');
};

/**
 * Update company
 */
export const updateCompany = async (id, companyData) => {
    try {
        console.log('[CompanyAPI] Updating tenant/company details:', id);
        const response = await apiClient.put(`/tenants/${id}`, {
            name: companyData.name,
            email: companyData.email,
            phone: companyData.phone,
            addressLine: companyData.addressLine,
            city: companyData.city,
            country: companyData.country || 'UAE',
            subdomain: companyData.subdomain,
            subscriptionPlan: (companyData.subscriptionPlan || 'basic').toUpperCase(),
            status: (companyData.status || 'active').toUpperCase(),
            subscriptionStartDate: companyData.subscriptionStartDate ? new Date(companyData.subscriptionStartDate).toISOString() : null,
            subscriptionExpiryDate: companyData.subscriptionExpiryDate ? new Date(companyData.subscriptionExpiryDate).toISOString() : null,
            maxUsers: companyData.maxUsers,
            maxOrders: companyData.maxOrders,
            maxProducts: companyData.maxProducts,
            tradeLicenseNo: companyData.tradeLicenseNo,
            businessType: companyData.businessType,
            category: companyData.category,
            description: companyData.description,
            contactPerson: companyData.contactPerson,
            whatsapp: companyData.whatsapp
        });
        return response.data;
    } catch (error) {
        console.error('[CompanyAPI] Error updating company:', error);
        throw error;
    }
};

/**
 * Delete company
 * TODO: Backend endpoint not yet available
 */
export const deleteCompany = async (id) => {
    console.warn('[CompanyAPI] deleteCompany not implemented in backend yet');
    throw new Error('Backend endpoint not yet available');
};
